import React, { useState, useEffect, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { StrictModeDroppable as StrictModeDroppable } from './StrictModeDroppable';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Divider,
  IconButton,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  List,
  ListItem,
  ListItemText,
  Paper
} from '@mui/material';
import {
  Close as CloseIcon,
  DragIndicator as DragIndicatorIcon,
  Add as AddIcon,
  TextFields as TextFieldsIcon,
  CheckBox as CheckBoxIcon,
  RadioButtonChecked as RadioButtonIcon,
  ErrorOutline as ErrorOutlineIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  DragIndicator as DragHandleIcon
} from '@mui/icons-material';
import axios from 'axios';
import config from '../../config';

const FIELD_TYPES = [
  { value: 'text', label: 'Short Answer', icon: <TextFieldsIcon /> },
  { value: 'textarea', label: 'Paragraph', icon: <TextFieldsIcon /> },
  { value: 'radio', label: 'Multiple Choice', icon: <RadioButtonIcon /> },
  { value: 'checkbox', label: 'Checkboxes', icon: <CheckBoxIcon /> },
  { value: 'dropdown', label: 'Dropdown', icon: <ArrowDropDownIcon /> },
];

const SimpleFormBuilder = ({
  open,
  onClose,
  eventId,
  onFormCreated,
  formId = null // For editing existing form
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    is_active: true,
    is_published: false,
    sections: [{
      title: 'Section 1',
      description: '',
      questions: []
    }]
  });

  const [currentQuestion, setCurrentQuestion] = useState({
    field: {
      label: '',
      field_type: 'text',
      required: true,
      options: ''
    },
    text: '',
    description: '',
    required: true,
    section_id: 0, // Track which section this question belongs to
    isEditing: false,
    tempText: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    questionIndex: null,
    questionId: null
  });

  // Set isEditing and load form data when formId or open state changes
  useEffect(() => {
    if (formId && (typeof formId === 'string' || (typeof formId === 'object' && formId.id))) {
      setIsEditing(true);
      // Reset form data to ensure we load fresh data
      setFormData({
        title: '',
        description: '',
        is_active: true,
        is_published: false,
        sections: [{
          title: 'Section 1',
          description: '',
          questions: []
        }]
      });
    } else {
      setIsEditing(false);
      // Reset form data for new form
      setFormData({
        title: '',
        description: '',
        is_active: true,
        is_published: false,
        sections: [{
          title: 'Section 1',
          description: '',
          questions: []
        }]
      });
    }
  }, [formId, open]);

  // Load form data if in edit mode
  useEffect(() => {
    const fetchForm = async () => {
      // Only fetch if we have a valid formId and the dialog is open
      if (!formId || !open) return;

      try {
        setLoading(true);
        setError('');
        const token = localStorage.getItem('access_token');

        // Skip if formId is not a valid ID (e.g., when creating a new form)
        if (!formId || (typeof formId === 'object' && !formId.id)) {
          setLoading(false);
          return;
        }

        const formIdValue = formId?.id || formId; // Handle both object and primitive ID
        console.log('Fetching form data for ID:', formIdValue);

        // First get the form details
        const formResponse = await axios.get(
          `${config.API_BASE_URL}/forms/event-forms/${formIdValue}/`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        let sectionsWithQuestions = [];
        const formResponseData = formResponse.data;

        // Check if questions are included in the form response
        if (formResponseData.questions && Array.isArray(formResponseData.questions)) {
          console.log('Form questions from form response:', formResponseData.questions);
          // Questions are included directly in the form response
          sectionsWithQuestions = [{
            id: 'default-section',
            title: 'Questions',
            description: '',
            order: 0,
            questions: formResponseData.questions.map(q => ({
              id: q.id,
              text: q.text || 'Untitled Question',
              description: q.description || '',
              required: q.required !== false,
              field: {
                id: q.field?.id,
                field_type: q.field?.field_type || 'text',
                label: q.field?.label || '',
                options: q.field?.options || '',
                required: q.required !== false
              },
              order: q.order || 0
            }))
          }];
        } else {
          // Fallback to separate API calls if questions not in form response
          try {
            // Try to get questions directly from form
            const questionsResponse = await axios.get(
              `${config.API_BASE_URL}/forms/event-forms/${formIdValue}/questions/`,
              {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              }
            );

            console.log('Questions API Response:', questionsResponse.data);

            if (questionsResponse.data && questionsResponse.data.length > 0) {
              sectionsWithQuestions = [{
                id: 'default-section',
                title: 'Questions',
                description: '',
                order: 0,
                questions: questionsResponse.data.map(q => ({
                  id: q.id,
                  text: q.text || 'Untitled Question',
                  description: q.description || '',
                  required: q.required !== false,
                  field: {
                    id: q.field?.id,
                    field_type: q.field?.field_type || 'text',
                    label: q.field?.label || '',
                    options: q.field?.options || '',
                    required: q.required !== false
                  },
                  order: q.order || 0
                }))
              }];
            }
          } catch (error) {
            console.warn('Could not load questions:', error);
          }
        }

        // Transform the API response to match our form structure
        const formData = {
          id: formResponseData.id,
          title: formResponseData.title,
          description: formResponseData.description || '',
          is_published: formResponseData.is_published || false,
          sections: sectionsWithQuestions.length ? sectionsWithQuestions : [{
            id: 'default-section',
            title: 'Section 1',
            description: '',
            order: 0,
            questions: []
          }]
        };

        console.log('Loaded form data:', formData);
        setFormData(formData);
        setIsEditing(true);
      } catch (error) {
        console.error('Error loading form:', error);
        setError('Failed to load form data. ' + (error.response?.data?.detail || error.message || ''));
      } finally {
        setLoading(false);
      }
    };

    fetchForm();
  }, [formId, open]);

  // Handle next step in the form
  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep(prevStep => prevStep + 1);
    }
  };

  // Handle previous step in the form
  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(prevStep => prevStep - 1);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleQuestionChange = (field, value) => {
    setCurrentQuestion(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle drag end event for reordering questions
  const onDragEnd = (result) => {
    if (!result.destination) return;

    const { source, destination } = result;

    // Don't do anything if dropped outside the list or in the same position
    if (destination.droppableId === source.droppableId &&
      destination.index === source.index) {
      return;
    }

    // Reorder questions in the section
    setFormData(prevData => {
      const updatedSections = [...prevData.sections];
      const sectionIndex = 0; // Only one section for now
      const section = { ...updatedSections[sectionIndex] };

      // Create a new array with the reordered questions
      const newQuestions = Array.from(section.questions || []);
      const [movedQuestion] = newQuestions.splice(source.index, 1);
      newQuestions.splice(destination.index, 0, movedQuestion);

      // Update the section with reordered questions
      updatedSections[sectionIndex] = {
        ...section,
        questions: newQuestions.map((q, idx) => ({
          ...q,
          order: idx
        }))
      };

      return {
        ...prevData,
        sections: updatedSections
      };
    });
  };

  // Set up question for editing
  const setupQuestionForEdit = (question) => {
    setCurrentQuestion({
      ...question,
      text: getQuestionLabel(question),
      field: {
        ...question.field,
        field_type: question.field?.field_type || 'text',
        options: Array.isArray(question.field?.options)
          ? question.field.options.join('\n')
          : question.field?.options || ''
      },
      section_id: 0 // Default to first section for now
    });

    // Scroll to the question form
    const formElement = document.getElementById('question-form');
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Save edited question
  const saveEditedQuestion = (sectionIndex, questionIndex) => {
    const updatedSections = [...formData.sections];
    const question = updatedSections[sectionIndex].questions[questionIndex];

    if (!currentQuestion.text || currentQuestion.text.trim() === '') {
      setError('Question text cannot be empty');
      return;
    }

    if (currentQuestion.id) {
      // Update existing question
      updatedSections[sectionIndex].questions = updatedSections[sectionIndex].questions.map(q => 
        q.id === currentQuestion.id 
          ? { 
              ...currentQuestion,
              field: {
                ...currentQuestion.field,
                label: currentQuestion.text
              },
              label: currentQuestion.text
            }
          : q
      );
    } else {
      // Add new question
      const newQuestion = {
        ...currentQuestion,
        id: `temp-${Date.now()}`,
        field: {
          ...currentQuestion.field,
          label: currentQuestion.text
        },
        label: currentQuestion.text
      };
      updatedSections[sectionIndex].questions = [...updatedSections[sectionIndex].questions, newQuestion];
    }

    // Reset current question
    setCurrentQuestion({
      field: {
        field_type: 'text',
        required: true,
        placeholder: '',
        help_text: '',
        options: ''
      },
      text: '',
      description: '',
      required: true,
      section_id: 0
    });

    setFormData(prev => ({
      ...prev,
      sections: updatedSections
    }));
  };

  // Handle delete click
  const handleDeleteClick = (index, id) => {
    setDeleteDialog({
      open: true,
      questionIndex: index,
      questionId: id
    });
  };

  // Handle delete cancel
  const handleDeleteCancel = () => {
    setDeleteDialog({
      open: false,
      questionIndex: null,
      questionId: null
    });
  };

  // Handle delete confirmation
  const handleDeleteConfirm = () => {
    if (deleteDialog.questionIndex !== null) {
      const updatedSections = [...formData.sections];
      updatedSections[0].questions = updatedSections[0].questions.filter(
        (_, i) => i !== deleteDialog.questionIndex
      );
      setFormData(prev => ({
        ...prev,
        sections: updatedSections
      }));
    }
    setDeleteDialog({
      open: false,
      questionIndex: null,
      questionId: null
    });
  };

  // Check if we're editing an existing question
  const isEditingQuestion = currentQuestion.id !== undefined;

  // Ensure we have at least one section
  useEffect(() => {
    if (formData.sections.length === 0) {
      setFormData(prev => ({
        ...prev,
        sections: [{
          title: 'Section 1',
          description: '',
          order: 1,
          questions: []
        }]
      }));
    }
  }, [formData.sections.length]);

  // Validate options for question types that require them
  useEffect(() => {
    if (['radio', 'checkbox', 'dropdown'].includes(currentQuestion.field?.field_type)) {
      const options = currentQuestion.field?.options
        ? String(currentQuestion.field.options).split('\n').filter(opt => opt.trim() !== '')
        : [];

      if (options.length === 0) {
        setError('Please add at least one option for this question type');
      } else if (error) {
        setError('');
      }
    }
  }, [currentQuestion.field?.field_type, currentQuestion.field?.options, error]);







  // Handle form submission
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Validate form data
      if (!formData.title || formData.title.trim() === '') {
        throw new Error('Form title is required');
      }

      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }

      // Flatten all questions from all sections into a single array
      const allQuestions = formData.sections.flatMap((section, sIndex) =>
        section.questions.map((q, qIndex) => {
          const questionText = q.text || q.label || `Question ${qIndex + 1}`;
          return {
            id: q.id && !q.id.toString().startsWith('temp-') ? q.id : null,
            label: questionText, // Required by the backend
            text: questionText,  // Keep for backward compatibility
            field_type: q.field?.field_type || 'text',
            required: q.required !== false,
            help_text: q.help_text || '',
            order: (sIndex * 100) + (q.order || qIndex), // Combine section and question order
            field: {
              id: q.field?.id || null,
              name: `question_${Date.now()}_${sIndex}_${qIndex}`,
              field_type: q.field?.field_type || 'text',
              required: q.required !== false,
              placeholder: q.field?.placeholder || '',
              help_text: q.help_text || '',
              options: q.field?.options
                ? (Array.isArray(q.field.options)
                  ? q.field.options
                  : String(q.field.options || '').split('\n').filter(Boolean))
                : []
            }
          };
        })
      );

      // Prepare the form payload with a flat questions array
      const formPayload = {
        title: formData.title.trim(),
        description: formData.description || '',
        is_active: true,
        is_published: true,
        event: eventId,
        questions: allQuestions
      };

      console.log('Submitting form data:', formPayload);

      // Set up headers
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Make the API request
      const method = isEditing ? 'put' : 'post';
      const url = isEditing
        ? `${config.API_BASE_URL}/forms/event-forms/${formId.id || formId}/`
        : `${config.API_BASE_URL}/forms/event-forms/`;

      const response = await axios({
        method,
        url,
        data: formPayload,
        headers
      });

      console.log('Form saved successfully:', response.data);
      setSuccess(isEditing ? 'Form updated successfully!' : 'Form created successfully!');

      // Reset form and close after delay
      setTimeout(() => {
        const newFormData = resetForm();

        // If we're editing, update the form with the new data
        if (isEditing && response.data) {
          // Convert the flat questions array back into sections for the form
          const questions = response.data.questions || [];
          const sectionsMap = new Map();

          // Group questions by section (default to a single section if no section info)
          questions.forEach((q, index) => {
            const sectionId = q.section_id || 0;
            if (!sectionsMap.has(sectionId)) {
              sectionsMap.set(sectionId, {
                id: sectionId,
                title: `Section ${sectionId + 1}`,
                description: '',
                order: sectionId,
                questions: []
              });
            }
            sectionsMap.get(sectionId).questions.push({
              id: q.id,
              text: q.text || q.label || `Question ${index + 1}`,
              label: q.label || q.text || `Question ${index + 1}`, // Ensure label is set
              field_type: q.field?.field_type || 'text',
              required: q.required !== false,
              help_text: q.help_text || '',
              order: q.order || index,
              field: {
                id: q.field?.id,
                field_type: q.field?.field_type || 'text',
                required: q.required !== false,
                placeholder: q.field?.placeholder || '',
                help_text: q.help_text || '',
                options: Array.isArray(q.field?.options)
                  ? q.field.options
                  : (q.field?.options ? [q.field.options] : [])
              }
            });
          });

          // Convert map to array and sort by section order
          const sections = Array.from(sectionsMap.values())
            .sort((a, b) => a.order - b.order);

          // If no sections were created, add a default one
          if (sections.length === 0) {
            sections.push({
              id: 0,
              title: 'Section 1',
              description: '',
              order: 0,
              questions: []
            });
          }

          setFormData({
            ...newFormData,
            id: response.data.id,
            title: response.data.title || '',
            description: response.data.description || '',
            is_published: response.data.is_published || false,
            sections: sections
          });
        }

        if (onFormCreated) {
          onFormCreated(response.data.id);
        }
      }, 1500);

    } catch (error) {
      console.error('Error saving form:', error);

      // Handle different types of errors
      let errorMessage = 'Failed to save form. ';

      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        if (error.response.data) {
          // Handle validation errors
          if (typeof error.response.data === 'object') {
            const errorMessages = [];
            for (const [key, value] of Object.entries(error.response.data)) {
              if (Array.isArray(value)) {
                errorMessages.push(`${key}: ${value.join(', ')}`);
              } else if (typeof value === 'string') {
                errorMessages.push(value);
              } else {
                errorMessages.push(JSON.stringify(value));
              }
            }
            errorMessage += errorMessages.join('\n');
          } else if (typeof error.response.data === 'string') {
            errorMessage += error.response.data;
          } else {
            errorMessage += 'An unknown error occurred';
          }
        } else {
          errorMessage += `Server responded with status ${error.response.status}`;
        }
      } else if (error.request) {
        // The request was made but no response was received
        errorMessage += 'No response from server. Please check your connection.';
      } else {
        // Something happened in setting up the request
        errorMessage += error.message || 'An error occurred';
      }

      setError(errorMessage);
      throw error; // Re-throw to be caught by the outer try-catch
    } finally {
      setLoading(false);
    }
  };

  // Reset form to initial state
  const resetForm = () => {
    const newFormData = {
      title: '',
      description: '',
      is_active: true,
      is_published: true,
      sections: [{
        title: 'Section 1',
        description: '',
        order: 0,
        questions: []
      }]
    };

    setFormData(newFormData);
    setCurrentQuestion({
      field: {
        field_type: 'text',
        required: true,
        placeholder: '',
        help_text: '',
        options: ''
      },
      text: '',
      description: '',
      required: true,
      section_id: 0,
      isEditing: false,
      tempText: ''
    });
    setError('');
    setSuccess('');
    setActiveStep(0);

    return newFormData;
  };

  const renderReviewStep = () => {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          {isEditing ? 'Review Form Updates' : 'Review Your Form'}
        </Typography>
        <Typography variant="body1" paragraph>
          {isEditing
            ? 'Please review your changes before updating the form.'
            : 'Please review your form before submitting. You can go back to make changes if needed.'
          }
        </Typography>

        <Box sx={{ mt: 3, mb: 4 }}>
          <Typography variant="subtitle1" gutterBottom sx={{
            fontWeight: 'bold',
            fontSize: '1.25rem',
            color: 'primary.main'
          }}>
            {formData.title || 'Untitled Form'}
          </Typography>
          {formData.description && (
            <Typography variant="body1" color="textSecondary" paragraph>
              {formData.description}
            </Typography>
          )}
        </Box>

        {formData.sections.map((section, sectionIndex) => {
          const hasQuestions = section.questions?.length > 0;
          return (
            <Box key={section.id || `section-${sectionIndex}`} sx={{ mb: 4 }}>
              {formData.sections.length > 1 && (
                <Typography variant="subtitle2" sx={{
                  fontWeight: 'bold',
                  mb: 2,
                  pb: 1,
                  borderBottom: '1px solid #e0e0e0'
                }}>
                  {section.title || `Section ${sectionIndex + 1}`}
                </Typography>
              )}

              {hasQuestions ? (
                section.questions.map((question, questionIndex) => {
                  const field = question.field || {};
                  const fieldType = field.field_type || question.field_type || 'text';

                  const getOptions = () => {
                    if (Array.isArray(field.options)) {
                      return field.options;
                    } else if (typeof field.options === 'string') {
                      return field.options.split('\n').filter(opt => opt.trim() !== '');
                    }
                    return [];
                  };

                  const renderQuestionPreview = () => {
                    const options = getOptions();

                    switch (fieldType) {
                      case 'text':
                      case 'textarea':
                        return (
                          <TextField
                            fullWidth
                            variant="outlined"
                            size="small"
                            placeholder={field.placeholder || 'Your answer here...'}
                            multiline={fieldType === 'textarea'}
                            rows={fieldType === 'textarea' ? 3 : 1}
                            disabled
                            sx={{ mt: 1 }}
                          />
                        );

                      case 'dropdown':
                        return (
                          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                            <Select
                              value=""
                              displayEmpty
                              renderValue={() => 'Select an option'}
                            >
                              {options.map((option, i) => (
                                <MenuItem key={i} value={option}>
                                  {option}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        );

                      case 'radio':
                      case 'checkbox':
                        return (
                          <FormControl component="fieldset" fullWidth sx={{ mt: 1 }}>
                            {options.map((option, i) => (
                              <FormControlLabel
                                key={i}
                                control={
                                  fieldType === 'checkbox' ? (
                                    <Checkbox disabled />
                                  ) : (
                                    <RadioButtonIcon color="action" />
                                  )
                                }
                                label={option}
                                sx={{
                                  ml: 0.5,
                                  '& .MuiFormControlLabel-label': {
                                    color: 'text.secondary',
                                    fontSize: '0.875rem'
                                  }
                                }}
                              />
                            ))}
                            {options.length === 0 && (
                              <Typography variant="caption" color="textSecondary">
                                No options defined
                              </Typography>
                            )}
                          </FormControl>
                        );

                      default:
                        return (
                          <Typography variant="body2" color="textSecondary">
                            Preview not available for this field type
                          </Typography>
                        );
                    }
                  };

                  return (
                    <Box
                      key={question.id || `question-${questionIndex}`}
                      sx={{
                        mb: 3,
                        p: 2,
                        backgroundColor: 'background.paper',
                        borderRadius: 1,
                        border: '1px solid #f0f0f0'
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: '500' }}>
                          {getQuestionLabel(question)}
                          {question.required && <span style={{ color: 'red', marginLeft: 4 }}>*</span>}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {fieldType.charAt(0).toUpperCase() + fieldType.slice(1)}
                        </Typography>
                      </Box>

                      {question.help_text && (
                        <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1.5 }}>
                          {question.help_text}
                        </Typography>
                      )}

                      {renderQuestionPreview()}

                      {field.description && (
                        <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1, fontStyle: 'italic' }}>
                          {field.description}
                        </Typography>
                      )}
                    </Box>
                  );
                })
              ) : (
                <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                  No questions added to this section yet.
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    );
  };

  const steps = ['Form Details', isEditing ? 'Edit Questions' : 'Add Questions', 'Review'];

  // Get question label with fallback to text or field.label
  const getQuestionLabel = (question) => {
    return question.label || question.text || question.field?.label || 'Untitled Question';
  };

  // Check if form is valid for the current step
  const isFormValid = () => {
    // For first step, only check if title is filled
    if (activeStep === 0) {
      return formData.title && formData.title.trim() !== '';
    }
    // For other steps, check both title and questions
    return formData.title && formData.title.trim() !== '' &&
      formData.sections.some(section => section.questions?.length > 0);
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Form Title"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Form Description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              margin="normal"
              multiline
              rows={3}
            />
          </Box>
        );
      case 1:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              {isEditing ? 'Edit Questions' : 'Add Questions'}
            </Typography>

            {/* Current Question Form */}
            <Box id="question-form" sx={{ p: 2, border: '1px solid #e0f0f0', borderRadius: 1, mb: 3, bgcolor: 'background.paper' }}>
              <Typography variant="subtitle1" gutterBottom>
                {currentQuestion.id ? 'Edit Question' : 'Add New Question'}
                {currentQuestion.id && (
                  <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                    (ID: {currentQuestion.id})
                  </Typography>
                )}
              </Typography>

              <TextField
                fullWidth
                label="Question Text"
                value={currentQuestion.text}
                onChange={(e) => handleQuestionChange('text', e.target.value)}
                margin="normal"
                required
              />

              <FormControl fullWidth margin="normal">
                <InputLabel>Question Type</InputLabel>
                <Select
                  value={currentQuestion.field?.field_type || 'text'}
                  onChange={(e) => handleQuestionChange('field', { ...currentQuestion.field, field_type: e.target.value })}
                  label="Question Type"
                >
                  {FIELD_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {type.icon}
                        {type.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={currentQuestion.required !== false}
                    onChange={(e) => handleQuestionChange('required', e.target.checked)}
                    color="primary"
                  />
                }
                label="Required"
                sx={{ mt: 1 }}
              />

              {['radio', 'checkbox', 'dropdown'].includes(currentQuestion.field?.field_type) && (
                <TextField
                  fullWidth
                  label="Options (one per line)"
                  value={currentQuestion.field?.options || ''}
                  onChange={(e) => handleQuestionChange('field', {
                    ...currentQuestion.field,
                    options: e.target.value
                  })}
                  margin="normal"
                  multiline
                  rows={4}
                  placeholder="Option 1&#10;Option 2&#10;Option 3"
                />
              )}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, gap: 1 }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => {
                    // Reset the form
                    setCurrentQuestion({
                      field: {
                        field_type: 'text',
                        required: true,
                        placeholder: '',
                        help_text: '',
                        options: ''
                      },
                      text: '',
                      description: '',
                      required: true,
                      section_id: 0
                    });
                  }}
                  disabled={!currentQuestion.text && !currentQuestion.id}
                >
                  Cancel
                </Button>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {currentQuestion.id && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => handleDeleteClick(0, currentQuestion.id)}
                    >
                      Delete
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => saveEditedQuestion(0, 0)}
                    disabled={!currentQuestion.text && !currentQuestion.id}
                  >
                    Save
                  </Button>
                </Box>
              </Box>
            </Box>

            {/* Questions List */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Questions
              </Typography>

              {formData.sections[0].questions.map((question, index) => (
                <Box key={question.id || `question-${index}`} sx={{ mb: 2, p: 2, border: '1px solid #f0f0f0', borderRadius: 1, bgcolor: 'background.paper' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: '500' }}>
                      {getQuestionLabel(question)}
                      {question.required && <span style={{ color: 'red', marginLeft: 4 }}>*</span>}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => setupQuestionForEdit(question)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => handleDeleteClick(index, question.id)}
                      >
                        Delete
                      </Button>
                    </Box>
                  </Box>

                  <Typography variant="body1" paragraph>
                    {getQuestionLabel(question)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        );
      case 2:
        return renderReviewStep();
      default:
        return <div>Not Found</div>;
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '80vh',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          if (activeStep === steps.length - 1) {
            handleSubmit();
          } else {
            handleNext();
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {isEditing ? 'Edit Form' : 'Create New Form'}
            </Typography>
            <IconButton edge="end" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {error && (
            <Box sx={{
              backgroundColor: 'error.main',
              color: 'error.contrastText',
              p: 1.5,
              borderRadius: 1,
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <ErrorOutlineIcon fontSize="small" />
              <Typography variant="body2">{error}</Typography>
            </Box>
          )}
          {success && (
            <Box sx={{
              backgroundColor: 'success.light',
              color: 'success.contrastText',
              p: 1.5,
              borderRadius: 1,
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <ErrorOutlineIcon fontSize="small" />
              <Typography variant="body2">{success}</Typography>
            </Box>
          )}
          <Stepper activeStep={activeStep} sx={{ mt: 2, mb: 2 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          {renderStepContent(activeStep)}
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between', borderTop: 1, borderColor: 'divider' }}>
          <Button
            onClick={activeStep === 0 ? onClose : handleBack}
            disabled={loading}
            startIcon={activeStep === 0 ? <CloseIcon /> : <ArrowBackIcon />}
            variant="outlined"
          >
            {activeStep === 0 ? 'Cancel' : 'Back'}
          </Button>
          <Box>
            {activeStep < steps.length - 1 ? (
              <Button
                type="button"
                variant="contained"
                color="primary"
                onClick={handleNext}
                disabled={loading || !isFormValid()}
                sx={{
                  minWidth: '120px',
                  '&:disabled': {
                    backgroundColor: 'action.disabledBackground',
                    color: 'text.disabled'
                  }
                }}
              >
                Next
              </Button>
            ) : (
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading || !isFormValid()}
                startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                sx={{
                  minWidth: '150px',
                  backgroundColor: 'success.main',
                  '&:hover': {
                    backgroundColor: 'success.dark',
                  },
                  '&:disabled': {
                    backgroundColor: 'action.disabledBackground',
                    color: 'text.disabled'
                  }
                }}
              >
                {loading ? 'Saving...' : 'Save Form'}
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">Delete Question</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this question? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SimpleFormBuilder;